import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import { Link } from 'react-router-dom';
import Title from './Title';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CardActionArea from '@mui/material/CardActionArea';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {TextField, InputAdornment, List, ListItem, ListItemText, Box} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import {Search as SearchIcon, Clear as ClearIcon} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';

const ChatbotList = ({ user, accessToken, listtype, listtitle, showall, morelink, createdbyinfo }) => {
  const [chatbots, setChatbots] = useState([]);
  const [filteredChatbots, setFilteredChatbots] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(16);
  const [selectedChatbot, setSelectedChatbot] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [sharedUsers, setSharedUsers] = useState([]);
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchChatbots = async () => {
      const bodyData = JSON.stringify({ access_token: accessToken, assistant_type: listtype.toString() });
      try {
        const response = await fetch(process.env.REACT_APP_API_BASE_URL + 'actions/get_assistants', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: bodyData
        });
        if (!response.ok) {
          throw new Error('Could not fetch chatbot data');
        }
        const data = await response.json();
        const chatbots = data.assistants.map(assistant => ({
          id: assistant.id,
          key: assistant.key,
          name: assistant.name,
          icon: './aioda_logo.png',
          description: assistant.description,
          createdby: assistant.createdby,
          canedit: assistant.canedit
        }));

        setChatbots(chatbots);
      } catch (error) {
        showErrorModal('Error fetching chatbots: ' + error);
      }
    };
    setPage(0);
    setSearchTerm('');
    fetchChatbots();    
  }, [listtype]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredChatbots(chatbots);
    } else {
      const filtered = chatbots.filter(chatbot =>
        chatbot.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chatbot.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (chatbot.createdby && chatbot.createdby.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredChatbots(filtered);
    }
    setPage(0);
  }, [searchTerm, chatbots]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event, cbot) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setSelectedChatbot(cbot);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedChatbot(null);
  };

  const handleDelete = async (event) => {
    event.stopPropagation();
    event.preventDefault();

    const bodyData = JSON.stringify({
      access_token: accessToken,
      assistant_key: selectedChatbot.key      
    });
    setOpenDialog(false);
    
    try {
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + 'actions/delete_assistant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: bodyData
      });

      const result = await response.json();

      if (result.status === "Success") {
        setChatbots((prevChatbots) =>
          prevChatbots.filter((chatbot) => chatbot.key !== selectedChatbot.key)
        );
      } else {
        showErrorModal('Failed to delete assistant: ' + result.error);
      }
    } catch (error) {
      showErrorModal('Error deleting assistant: ' + error);
    }

    handleMenuClose();
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    handleMenuClose();
  };

  const handleEditClick = (chatbotKey) => {
    navigate(`/edit/${chatbotKey}`);
  };

  const handleCopyLink = async (chatbotKey) => {
    const origin = window.location.origin + window.location.pathname.split('#')[0];
    const hashPath = `/assistant/${chatbotKey}`;
    const fullUrl = `${origin}#${hashPath}`;
    try {      
      await navigator.clipboard.writeText(fullUrl);
      handleMenuClose();
      showInfoModal(`Link copied to clipboard! Make sure the visibility of the assistant is set to Public or Unlisted if you want other users to access it. ${fullUrl}`);
    } catch (err) {
      showErrorModal("Failed to copy the link. Please try again.");
    }
  };

  const handleShareClick = async (chatbotKey) => {
    setShareModalOpen(true);
    //handleMenuClose();
    try {
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + "actions/get_share_info", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_token: accessToken,
          assistant_key: chatbotKey
        })
      });

      const data = await response.json();
      if (data.status === "Success") {
        const sharedList = data.sharedemails.map(email => ({ email }));
        setSharedUsers(sharedList);
      } else {
        showErrorModal("Failed to fetch shared users. Try again later.");
      }
    } catch (error) {
      showErrorModal("Error loading shared users: " + error);
    }
  };

  const showErrorModal = (message) => {
    setErrorMessage(message);
    setErrorModalOpen(true);
  };

  const showInfoModal = (message) => {
    setInfoMessage(message);
    setInfoModalOpen(true);
  };

  const handleAddSharedUser = async (chatbotKey) => {
    if (!shareEmail || sharedUsers.some(u => u.email === shareEmail)) return;
    try {
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + "actions/share_assistant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_token: accessToken,
          assistant_key: chatbotKey,
          shared_email: shareEmail
        })
      });

      const data = await response.json();
      if (data.status === "Success") {
        setSharedUsers([...sharedUsers, { email: shareEmail }]);
        setShareEmail("");
      } else {
        showErrorModal("Sharing failed. Try again later.");
      }
    } catch (error) {
      showErrorModal("Error sharing assistant: " + error);
    }
  };

  const handleRemoveUser = async (email, chatbotKey) => {
    try {
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + "actions/unshare_assistant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_token: accessToken,
          assistant_key: chatbotKey,
          shared_email: email
        })
      });

      const data = await response.json();
      if (data.status === "Success") {
        setSharedUsers(sharedUsers.filter(u => u.email !== email));
      } else {
        showErrorModal("Unsharing failed. Try again later.");
      }
    } catch (error) {
      showErrorModal("Error unsharing assistant: " + error);
    }
  };


  const handleCloseShareModel = () => {
    setShareModalOpen(false);
    handleMenuClose();
  }

  const displayChatbots = showall
    ? filteredChatbots.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : (filteredChatbots.length >= 4 ? filteredChatbots.slice(0, 3) : filteredChatbots);

  return (
    <div>
      <Title>{listtitle}</Title>
      {showall && (
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={`Search ${listtitle}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setSearchTerm('')}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {searchTerm && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Found {filteredChatbots.length} assistant{filteredChatbots.length !== 1 ? 's' : ''} matching "{searchTerm}"
            </Typography>
          )}
        </Box>
      )}
      {filteredChatbots.length === 0 && !searchTerm ? (
        <Typography variant="h6" color="text.secondary" align="center">
          {listtype === 2 && ("There are no official BUas AI Assistants yet...")}
          {listtype === 1 && ("There are no Community AI Assistants yet...")}
          {listtype === 0 && (
            <>
              You don't have any AI Assistant yet. Use the 
              <AddCircleOutlineIcon style={{ verticalAlign: 'middle', marginLeft: '4px', marginRight: '4px' }} />
              button to create your own AI Assistant.
            </>
          )}
          {listtype === 3 && ("There are no AI Assistants shared with you yet...")}
        </Typography>
      ) : filteredChatbots.length === 0 && searchTerm ? (
        <Typography variant="h6" color="text.secondary" align="center">
          No AI Assistants found matching "{searchTerm}"
          <br />
          <Button 
            variant="text" 
            onClick={() => setSearchTerm('')}
            sx={{ mt: 1 }}
          >
            Clear search
          </Button>
        </Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {displayChatbots.map((chatbot, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    position: 'relative', 
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f0f0f0',
                      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                >
                  <CardActionArea component={Link} to={`/assistant/${chatbot.key}`}>
                    <CardHeader
                      avatar={<Avatar src={chatbot.icon} />}
                      title={chatbot.name}
                      titleTypographyProps={{ variant: 'subtitle1' }}
                      sx={{
                        paddingRight: '48px',
                      }}
                    />
                    <CardContent>
                      {createdbyinfo && chatbot.createdby && (
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            display: 'block', 
                            marginBottom: 1,
                            marginTop: -2,
                            fontStyle: 'italic'
                          }}
                        >
                          Created by {chatbot.createdby}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {chatbot.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                  {chatbot.canedit === 1 && (
                    <>
                      <IconButton
                        edge="end"
                        aria-label="more"
                        onClick={(event) => {
                          event.stopPropagation();  
                          handleMenuOpen(event, chatbot);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 5,
                          right: 14,
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                      >
                        <MenuItem onClick={() => handleCopyLink(selectedChatbot.key)}>Copy Link</MenuItem>                        
                        <MenuItem onClick={() => handleShareClick(selectedChatbot.key)}>Share Assistant</MenuItem>
                        <MenuItem onClick={() => handleEditClick(selectedChatbot.key)}>Edit Assistant</MenuItem>
                        <MenuItem onClick={handleOpenDialog}>Delete Assistant</MenuItem>
                      </Menu>
                    </>
                  )}
                </Card>
              </Grid>
            ))}
            {!showall && chatbots.length >= 4 && (
              <Grid item xs={12} sm={6} md={3}>
                <Link to={morelink} style={{ textDecoration: 'none' }}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      '&:hover': {
                        backgroundColor: '#e0e0e0',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
                      }
                    }}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                      <MoreHorizIcon sx={{ fontSize: 40, color: '#757575' }} />
                      <Typography variant="subtitle1" color="text.secondary">
                        View More
                      </Typography>
                    </CardContent>
                  </Card>
                </Link>
              </Grid>
            )}
          </Grid>
          <Dialog open={openDialog} onClose={handleCloseDialog}>
            <DialogTitle>Delete AI Assistant</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete the AI Assistant "{selectedChatbot?.name}"?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} color="primary">
                Cancel
              </Button>
              <Button onClick={handleDelete} color="secondary">
                Delete
              </Button>
            </DialogActions>
          </Dialog>
          {showall && (
            <TablePagination
              component="div"
              count={filteredChatbots.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[16, 32, 64, 128]}
            />
          )}
        </>
      )}
      {errorModalOpen && (
        <Dialog
          open={errorModalOpen}
          onClose={() => setErrorModalOpen(false)}
          aria-labelledby="error-dialog-title"
          aria-describedby="error-dialog-description"
        >
          <DialogTitle id="error-dialog-title">Error</DialogTitle>
          <DialogContent id="error-dialog-description">
            <p>{errorMessage ?? "An unexpected error occurred."}</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setErrorModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      {infoModalOpen && (
        <Dialog
          open={infoModalOpen}
          onClose={() => setInfoModalOpen(false)}
          aria-labelledby="info-dialog-title"
          aria-describedby="info-dialog-description"
        >
          <DialogTitle id="info-dialog-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <InfoOutlinedIcon color="primary" />
              <span>Information</span>
            </div>
          </DialogTitle>
          <DialogContent id="info-dialog-description">
            {infoMessage ? (
              <p>
                {infoMessage.split(/(https?:\/\/[^\s]+)/g).map((part, index) =>
                  part.match(/^https?:\/\//) ? (
                    <span
                      key={index}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}
                    >
                      <CheckCircleOutlineIcon color="primary" />
                      <a href={part} target="_blank" rel="noopener noreferrer">
                        {part}
                      </a>
                    </span>
                  ) : (
                    <span key={index}>{part}</span>
                  )
                )}
              </p>
            ) : (
              <p>An unexpected information.</p>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInfoModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      <Dialog open={shareModalOpen} 
              onClose={() => setShareModalOpen(false)}
              fullWidth
              maxWidth="sm">
        <DialogTitle>Share AI Assistant</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2, // spacing between items
              marginTop: 2,
              marginBottom: 2
            }}
          >
            <TextField
              fullWidth
              label="User email"
              placeholder="example@domain.com"
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
            />
            <Button
              variant="contained"
              onClick={() => handleAddSharedUser(selectedChatbot.key)}
              disabled={!shareEmail}
              sx={{ whiteSpace: 'nowrap' }} // prevent label wrapping
            >
              Share
            </Button>
          </Box>
          <h4 style={{ marginTop: 20 }}>Currently shared with:</h4>
          <List dense>
            {sharedUsers.map((user, index) => (
              <ListItem key={index}
                secondaryAction={
                  <IconButton edge="end" onClick={() => handleRemoveUser(user.email, selectedChatbot.key)}>
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText primary={user.email} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShareModel}>Close</Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default ChatbotList;
